import DraggableStepCard from 'pages/AutomationEngine/Engine/Steps/DraggableStepCard';
import type {
  FlowLoadErrors,
  GenericOption,
  PrimaryObjectUploadStep,
  SmartConnectorFlowLoad,
  SmartConnectorStatus,
} from 'pages/SmartConnectors/types';
import { type FC, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FlowBuilderContext } from '../../context';
import { useHistory } from 'react-router-dom';
import {
  PRIMARY_OBJECT_UPLOAD_STEPS,
  SMART_CONNECTOR_STATUSES,
  SMARTCONNECTORS_PATHNAME,
} from 'pages/SmartConnectors/constants';
import SmartConnectorService from 'services/SmartConnectorService';
import useModal from '__components/Modals/useModal';
import ConfirmationModal from '__components/Modals/ConfirmationModal';
import { useToast, toastVariant } from '__components/ToastProvider';
import { FlowNode } from '../../types';
import { getIsSmartConnectorActive } from 'pages/SmartConnectors/helpers';
import { UploadObjectScopeModal } from '../../UploadRelatedObjectModal';
import { isCSVLoadStep } from 'pages/SmartConnectors/SmartConnector/helpers';
import { useScope } from '__pages/SmartConnectors/SmartConnector/hooks/useScope';

export const UploadPrimaryObjectCard: FC<{
  step: FlowNode;
  color: string;
}> = ({ step, color, ...others }) => {
  const { t } = useTranslation();
  const history = useHistory<{
    errors?: FlowLoadErrors;
    scope?: string;
    step?: string;
  }>();
  const [showToast] = useToast();
  const {
    smartConnector,
    customObject,
    customObjects,
    refetchSmartConnector,
    flowErrors,
    setFlowErrors,
    showDeactivationModalWithResult,
  } = useContext(FlowBuilderContext);

  const { scopeOptions } = useScope(smartConnector, true);

  const [uploadObject, menuOptions] = useMemo(() => {
    const uploadObject =
      customObjects.find(({ id }) => id === step.details.custom_object) || null;

    const menuOptions = [
      {
        label: t('Edit'),
        value: 'edit',
      },
      step.details.custom_object && {
        label: t('Delete'),
        value: 'delete',
      },
    ].filter(Boolean);
    return [uploadObject, menuOptions];
  }, [step.details.custom_object, customObjects, t]);

  const stepErrors = flowErrors.flow.loads?.[step.order] || {};
  const hasErrors = Object.values(stepErrors).length;

  const onDelete = async (status: SmartConnectorStatus) => {
    try {
      await SmartConnectorService.updateSmartConnector(smartConnector.id, {
        flow: {
          loads: smartConnector.flow.loads.reduce(
            (acc, load) => {
              if (load.id !== step.details.id) {
                // filter out load to be deleted. We don't want to touch other loads, but we need to pass them to update order,
                // so we pass only id, custom_object and order for rest values to not to be touched and validated on BE
                acc.loads.push({
                  id: load.id,
                  order: acc.count++,
                  ...(isCSVLoadStep(load)
                    ? { custom_object: load.custom_object }
                    : {}),
                });
              }
              return acc;
            },
            { loads: [], count: 0 } as {
              loads: Partial<SmartConnectorFlowLoad>[];
              count: number;
            }
          ).loads,
        },
        status,
      });
      setFlowErrors((prev) => ({
        ...prev,
        flow: {
          ...prev.flow,
          loads: (prev.flow.loads || []).filter((_, i) => i !== step.order),
        },
      }));
      await refetchSmartConnector();
      return true;
    } catch (error) {
      showToast({
        message: t('Failed to delete upload step'),
        variant: toastVariant.FAILURE,
      });
      return false;
    }
  };

  const onConfirmDelete = () => {
    if (
      getIsSmartConnectorActive({
        status: smartConnector.status,
      })
    ) {
      return showDeactivationModalWithResult({
        handleSave: () => onDelete(SMART_CONNECTOR_STATUSES.inactive),
      });
    }
    return onDelete(smartConnector.status);
  };

  const [modalProps, , { show: showModal }] = useModal({
    handleSubmit: onConfirmDelete,
  });

  const [uploadModalProps, , { show: showUploadModal }] = useModal({});

  const onSelectMenu = ({ value }: GenericOption) => {
    if (value === 'edit') {
      let wizardStep: PrimaryObjectUploadStep =
        PRIMARY_OBJECT_UPLOAD_STEPS.matching;
      if (stepErrors.matching_rules) {
        wizardStep = PRIMARY_OBJECT_UPLOAD_STEPS.matching;
      } else if (stepErrors.field_mapping_rules) {
        wizardStep = PRIMARY_OBJECT_UPLOAD_STEPS.mapping;
      } else if (stepErrors.execution_variable) {
        wizardStep = PRIMARY_OBJECT_UPLOAD_STEPS.preview;
      } else if (step.details.field_mapping_rules?.length) {
        wizardStep = PRIMARY_OBJECT_UPLOAD_STEPS.mapping;
      }

      if (
        !step.details.scope ||
        !scopeOptions.some(({ value }) => value === step.details.scope)
      ) {
        // if scope is not set yet or is invalid we need to set it in modal before uploading object
        // if there is only one available scope we set it directly here
        if (scopeOptions.length > 1) {
          showUploadModal();
        } else {
          history.push(
            `${SMARTCONNECTORS_PATHNAME}/${smartConnector.id}/builder/upload-object/${step.details.custom_object || smartConnector.custom_object}/${step.details.id || 'new'}`,
            {
              scope: scopeOptions[0].value,
              errors: stepErrors,
              step: wizardStep,
            }
          );
        }
      } else {
        history.push(
          `${SMARTCONNECTORS_PATHNAME}/${smartConnector.id}/builder/upload-object/${step.details.custom_object || smartConnector.custom_object}/${step.details.id || 'new'}`,
          { errors: stepErrors, step: wizardStep }
        );
      }
    }
    if (value === 'delete') {
      showModal();
    }
  };

  return (
    <>
      <DraggableStepCard
        {...others}
        id={step.id}
        color={hasErrors ? 'var(--color-button-warning-hover)' : color}
        borderColor={hasErrors ? 'var(--color-button-warning-hover)' : null}
        showMovingPlaceholder={false}
        title={t('Upload {{objectName}}', {
          objectName: uploadObject?.objectName || customObject?.objectName,
        })}
        menuOptions={menuOptions}
        onSelectMenu={onSelectMenu}
        isSmartConnectorFlow
      >
        {t('{{amount}} Mapped Fields {{fromScope}}', {
          amount: step.details.field_mapping_rules?.length || 0,
          fromScope:
            step.details.scope &&
            t('from {{scope}}', { scope: step.details.scope }),
        })}
      </DraggableStepCard>
      {modalProps.show ? (
        <ConfirmationModal
          heading={t('Please Confirm Deletion')}
          {...modalProps}
        >
          {t(
            'This will delete the Load step, its configuration, and the associated upload execution variable.'
          )}
        </ConfirmationModal>
      ) : null}
      {uploadModalProps.show ? (
        <UploadObjectScopeModal
          {...uploadModalProps}
          loadId={step.details.id}
        />
      ) : null}
    </>
  );
};
